"use client";

import { useTranslations } from "next-intl";
import Image from "next/image";

import { Link } from "@/i18n/navigation";

import { buttonVariants } from "./ui/button-variants";
import { Heading1, Heading2, TypographyP } from "./ui/typography";

interface ErrorPageProps {
  statusCode?: string;
  title?: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
}

export function ErrorPage({
  statusCode = "404",
  title,
  description,
  actionLabel,
  onAction,
}: ErrorPageProps = {}) {
  const t = useTranslations("ErrorPage");

  return (
    <div className="h-screen overflow-hidden">
      <div className="relative size-full py-16">
        <header className="absolute top-[4%] left-[7%]">
          <Link href="/">
            <Image
              height={40}
              width={152}
              src="/assets-next/wayflyer-logo-v2.svg"
              alt="wayflyer logo"
              priority
            />
          </Link>
        </header>
        <main className="flex size-full items-center justify-center px-4 md:px-0">
          <section className="max-w-lg text-center">
            <Heading1 className="text-[160px] leading-[1.09] md:text-[160px]">
              {statusCode}
            </Heading1>
            <Heading2>{title ?? t("pageNotFound")}</Heading2>
            <TypographyP className="text-body mt-1">
              {description ?? (
                <>
                  {t("description")}{" "}
                  <Link
                    href="/"
                    className="font-normal underline underline-offset-[6px] hover:decoration-[3px]"
                  >
                    wayflyer.com
                  </Link>
                </>
              )}
            </TypographyP>
            <div className="mt-5">
              {onAction ? (
                <button
                  type="button"
                  className={buttonVariants()}
                  onClick={onAction}
                >
                  {actionLabel ?? t("goBack")}
                </button>
              ) : (
                <Link href="/" className={buttonVariants()}>
                  {actionLabel ?? t("goBack")}
                </Link>
              )}
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}
